/**
 * Recording a DOT inspection on an asset — TRBUD-342.
 *
 * Plan: docs/plans/trbud-342-record-dot-inspection.md. Until this, nothing in
 * TruckBuddy could say an inspection was DONE: the ten imported MaintainX due
 * dates would go stale the day each inspection was carried out and read overdue
 * for ever.
 *
 * The certificate (`asset_compliance_certificate`) is the record; an uploaded
 * report is its evidence. Saving writes one new certificate whose due date is
 * the date done plus the requirement's renewal period (12 months for
 * `dot_annual`), computed by `record_compliance_inspection` and never typed. Both
 * readers (the PM & DOT card and each asset's DOT field) already take the latest
 * unvoided due date, so they move with nothing else changed.
 *
 * Browser-safe: zod, types and constants.
 */
import { z } from "zod";
import { boundedDateString } from "./field-shapes.js";
import { idempotencyKey, strictObject } from "./requests.js";

/** The requirement this feature records against. Seeded by `seed:config`. */
export const DOT_REQUIREMENT_CODE = "dot_annual";

/**
 * What a person may record (decision 2's default): a pass, or a pass with
 * defects. A failed inspection is a work order for the defects; the pass is
 * recorded when the unit is re-inspected. The table also holds `fail` and
 * `unknown` (the imported rows are `unknown`), which is why `DotInspectionRow`
 * carries the wider type.
 */
export const recordableInspectionResults = ["pass", "pass_with_defects"] as const;
export type RecordableInspectionResult = (typeof recordableInspectionResults)[number];
export type DotInspectionResult = RecordableInspectionResult | "fail" | "unknown";

export const dotInspectionResultLabels: Record<DotInspectionResult, string> = {
  pass: "Pass",
  pass_with_defects: "Pass with defects",
  fail: "Fail",
  unknown: "Not recorded",
};

/** Field bounds, shared by the form's `maxLength` and the body schema. */
export const INSPECTION_PROVIDER_NAME_MAX = 200;
export const INSPECTION_CERTIFICATE_NUMBER_MAX = 100;
export const INSPECTION_DEFECT_NOTE_MAX = 2000;
export const INSPECTION_VOID_REASON_MIN = 3;
export const INSPECTION_VOID_REASON_MAX = 500;

/**
 * `POST /assets/:id/dot-inspections`.
 *
 * `performedOn` is a `recorded` date: not after today in the fleet zone and not
 * more than five years back, the same window `DateField purpose="recorded"`
 * shows and the routine enforces. Defects are required for a pass with defects.
 * The report file is optional (decision 4); when given it must already be on
 * the asset, uploaded through the ordinary asset document route.
 */
export const recordDotInspectionBody = strictObject({
  performedOn: boundedDateString("recorded"),
  result: z.enum(recordableInspectionResults),
  providerVendorId: z.string().uuid().optional(),
  providerName: z.string().trim().max(INSPECTION_PROVIDER_NAME_MAX).optional(),
  certificateNumber: z.string().trim().max(INSPECTION_CERTIFICATE_NUMBER_MAX).optional(),
  defectNote: z.string().trim().max(INSPECTION_DEFECT_NOTE_MAX).optional(),
  documentId: z.string().uuid().optional(),
  workOrderId: z.string().uuid().optional(),
  idempotencyKey,
}).superRefine((body, ctx) => {
  if (body.result === "pass_with_defects" && (body.defectNote ?? "").trim() === "") {
    ctx.addIssue({
      code: "custom",
      path: ["defectNote"],
      message: "Say which defects were found when an inspection passes with defects.",
    });
  }
});
export type RecordDotInspectionBody = z.infer<typeof recordDotInspectionBody>;

/** `POST /assets/:id/dot-inspections/:certificateId/void`. */
export const voidDotInspectionBody = strictObject({
  reason: z.string().trim().min(INSPECTION_VOID_REASON_MIN).max(INSPECTION_VOID_REASON_MAX),
  idempotencyKey,
});
export type VoidDotInspectionBody = z.infer<typeof voidDotInspectionBody>;

/** One certificate on the asset's DOT inspection panel. */
export interface DotInspectionRow {
  id: string;
  /** The date the inspection was done. Null on an imported due date with no inspection behind it. */
  performedOn: string | null;
  /** The due date this certificate sets. */
  expiresOn: string;
  result: DotInspectionResult;
  /** Who did it, as typed. */
  providerName: string | null;
  /** The vendor picked as the inspector, when one was. */
  vendor: { id: string; name: string } | null;
  certificateNumber: string | null;
  defectNote: string | null;
  /**
   * The report. Null when none was attached, and null for a role that may not
   * list an asset's papers (requester, viewer), as `GET /assets/:id/documents`
   * refuses them. `canPreview` is the content route's own answer.
   */
  document: { documentId: string; fileName: string | null; canPreview: boolean } | null;
  workOrder: { id: string; displayId: string } | null;
  /** `mainx` for the imported due dates, `truckbuddy` for one recorded here. */
  source: "mainx" | "truckbuddy";
  recordedBy: string | null;
  recordedAt: string;
  voided: { at: string; by: string | null; reason: string } | null;
  /**
   * Whether Void is offered: a TruckBuddy record that is not voided yet. The
   * server's answer, so the screen holds no copy of the rule. Imported records
   * are final (decision 5, the TRBUD-313 rule).
   */
  voidable: boolean;
}

/** `calculateComplianceStatus`'s states, in the fleet zone, as the PM & DOT card counts them. */
export type DotInspectionState = "not_due" | "due_soon" | "due" | "overdue";

/** `GET /assets/:id/dot-inspections`. */
export interface AssetDotInspections {
  /** Null when no active `dot_annual` requirement exists (run `seed:config`). */
  requirement: { code: string; name: string; renewalMonths: number; leadDays: number } | null;
  /**
   * False for a forklift (decision 3), a company vehicle (TRBUD-348 decision 2), or when no
   * requirement exists, with the sentence the panel prints.
   */
  canRecordHere: boolean;
  notRecordableReason: string | null;
  /** The certificate the readers use: the latest unvoided due date. */
  current: DotInspectionRow | null;
  /** The current certificate's state. Null when there is no certificate: no record is not evidence of lateness. */
  status: { state: DotInspectionState; remainingDays: number } | null;
  /** Newest first, voided rows included and marked. */
  history: DotInspectionRow[];
  /**
   * The asset papers already backing an unvoided certificate, so the panel can
   * offer "Record as inspection" only on a report nobody has recorded yet.
   */
  recordedDocumentIds: string[];
}
